// Lecture d'un fichier CSV de QCM.
//
// Format attendu (une ligne = une question) :
//   question ; choix_a ; choix_b ; choix_c ; choix_d ; bonne_reponse ; explication
//
// - séparateur « ; » (Excel en français) ou « , » : détecté automatiquement
// - de 2 à 10 colonnes de choix (choix_a … choix_j), les choix vides sont ignorés
// - bonne_reponse : une lettre (B) ou plusieurs (A|C, A,C ou A C) ; les chiffres 1, 2, 3… marchent aussi
// - explication : facultative, affichée après la correction
// - fichier enregistré en UTF-8 ou en « CSV (séparateur : point-virgule) » d'Excel (Windows-1252)

export const LETTERS = 'ABCDEFGHIJ';

// Lit le fichier en essayant l'UTF-8, puis l'encodage Windows d'Excel si les accents sont illisibles
export async function readCsvFile(file) {
  const buf = await file.arrayBuffer();
  let text;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    text = new TextDecoder('windows-1252').decode(buf);
  }
  return text.replace(/^﻿/, '');
}

function detectDelimiter(text) {
  const firstLine = text.split(/\r?\n/).find(l => l.trim() !== '') || '';
  const count = (ch) => {
    let n = 0, inQuotes = false;
    for (const c of firstLine) {
      if (c === '"') inQuotes = !inQuotes;
      else if (c === ch && !inQuotes) n++;
    }
    return n;
  };
  const candidates = [';', ',', '\t'].map(ch => [ch, count(ch)]).sort((a, b) => b[1] - a[1]);
  return candidates[0][1] > 0 ? candidates[0][0] : ';';
}

// Découpe le texte en lignes/cellules, en gérant les guillemets ("texte ; avec séparateur", "" pour un guillemet)
// Renvoie [{ line: numéro de ligne dans le fichier, cells: [...] }]
export function parseCsv(text) {
  const delim = detectDelimiter(text);
  const rows = [];
  let cells = [], cell = '', inQuotes = false, line = 1, rowStart = 1;
  const pushRow = () => {
    cells.push(cell);
    rows.push({ line: rowStart, cells });
    cells = []; cell = '';
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else {
        if (c === '\n') line++;
        cell += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delim) {
      cells.push(cell); cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      pushRow();
      line++;
      rowStart = line;
    } else {
      cell += c;
    }
  }
  if (cell !== '' || cells.length) pushRow();
  return { delimiter: delim, rows: rows.filter(r => r.cells.some(c => c.trim() !== '')) };
}

const norm = (s) => s.trim().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const QUESTION_COLS = ['question', 'enonce', 'intitule', 'questions'];
const CORRECT_COLS = ['bonne_reponse', 'bonnes_reponses', 'reponse_correcte', 'reponses_correctes', 'reponse', 'correct', 'solution', 'answer'];
const EXPLANATION_COLS = ['explication', 'explications', 'explanation', 'commentaire', 'correction'];
const isOptionCol = (h) => /^(choix|option|proposition)(_?[a-j0-9]+)?$/.test(h) || /^reponse_[a-j0-9]$/.test(h);

// Transforme les lignes du CSV en questions. Renvoie { questions, errors: [{ line, message }] }
export function csvToQuestions(text) {
  const { rows } = parseCsv(text);
  const errors = [];
  if (rows.length === 0) return { questions: [], errors: [{ line: 1, message: 'Le fichier est vide' }] };

  const header = rows[0].cells.map(norm);
  const qCol = header.findIndex(h => QUESTION_COLS.includes(h));
  const cCol = header.findIndex(h => CORRECT_COLS.includes(h));
  const eCol = header.findIndex(h => EXPLANATION_COLS.includes(h));
  const optCols = header.map((h, i) => (isOptionCol(h) ? i : -1)).filter(i => i >= 0);

  const missing = [];
  if (qCol < 0) missing.push('« question »');
  if (cCol < 0) missing.push('« bonne_reponse »');
  if (optCols.length < 2) missing.push('au moins deux colonnes de choix (« choix_a », « choix_b »)');
  if (missing.length) {
    return { questions: [], errors: [{ line: 1, message: `Ligne d'en-tête : colonne(s) manquante(s) ${missing.join(', ')}. En-tête attendu : question;choix_a;choix_b;choix_c;choix_d;bonne_reponse;explication` }] };
  }
  if (optCols.length > LETTERS.length) {
    errors.push({ line: 1, message: `${LETTERS.length} colonnes de choix maximum (seules les ${LETTERS.length} premières sont lues)` });
  }

  const questions = [];
  rows.slice(1).forEach(({ line, cells }) => {
    const get = (i) => (i >= 0 && i < cells.length ? cells[i].trim() : '');
    const text = get(qCol);
    // choix dans l'ordre des colonnes : A = 1re colonne de choix, B = 2e, etc.
    const options = optCols.slice(0, LETTERS.length).map(i => ({ text: get(i), correct: false }));
    const rawCorrect = get(cCol);
    const lineErrors = [];

    if (!text) lineErrors.push('la question est vide');

    const tokens = rawCorrect.toUpperCase().split(/[\s,;|/+]+/).filter(Boolean);
    if (tokens.length === 0) lineErrors.push('la colonne bonne_reponse est vide');
    tokens.forEach(t => {
      let idx = -1;
      if (/^[A-J]$/.test(t)) idx = LETTERS.indexOf(t);
      else if (/^\d+$/.test(t)) idx = Number(t) - 1;
      if (idx < 0 || idx >= options.length) {
        lineErrors.push(`bonne réponse « ${t} » inconnue (utilisez A, B, C…)`);
      } else if (!options[idx].text) {
        lineErrors.push(`la bonne réponse « ${LETTERS[idx]} » correspond à un choix vide`);
      } else {
        options[idx].correct = true;
      }
    });

    const filled = options.filter(o => o.text !== '');
    if (filled.length < 2) lineErrors.push('il faut au moins 2 choix remplis');

    if (lineErrors.length) {
      errors.push({ line, message: lineErrors.join(' ; ') });
    } else {
      questions.push({ text, explanation: get(eCol), options: filled });
    }
  });

  if (questions.length === 0 && errors.length === 0) {
    errors.push({ line: 2, message: 'Aucune question trouvée sous la ligne d\'en-tête' });
  }
  return { questions, errors };
}
